import { useState, useCallback } from 'react';
import { authenticatedFetch } from '../../../../utils/api';

export const useRosterOperations = (params, deviceInfo) => {
    const [rosterData, setRosterData] = useState([]);
    const [rosterSummary, setRosterSummary] = useState([]);
    const [pendingShifts, setPendingShifts] = useState([]);
    const [completedPendingIds, setCompletedPendingIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [futureDateRestrict, setFutureDateRestrict] = useState(false);
    const [noRosterDataMsg, setNoRosterDataMsg] = useState(null);
    const [toast, setToast] = useState({ visible: false, title: '', message: '' });

    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const showToast = useCallback((title, message) => {
        setToast({ visible: true, title, message });
    }, []);

    const hideToast = useCallback(() => {
        setToast({ visible: false, title: '', message: '' });
    }, []);

    const markPendingAsCompleted = useCallback((pendingIds) => {
        const idsArray = Array.isArray(pendingIds) ? pendingIds : [pendingIds];
        setCompletedPendingIds(prev => {
            const newSet = new Set(prev);
            idsArray.forEach(id => newSet.add(id));
            return newSet;
        });
    }, []);

    const clearCompletedPendingShifts = useCallback(() => {
        if (completedPendingIds.size > 0) {
            setPendingShifts(prev => prev.filter(p => !completedPendingIds.has(p.pendingId)));
            setRosterData(prev => prev.filter(p => !p.isPending || !completedPendingIds.has(p.pendingId)));
            setCompletedPendingIds(new Set());
        }
    }, [completedPendingIds]);

    const fetchRoster = useCallback(async (startDate, finishDate, siteName) => {
        if (!deviceInfo) return;

        setLoading(true);
        try {
            const res = await authenticatedFetch('/ManagerReport/GetRoster', {
                method: 'POST',
                body: JSON.stringify({
                    DateFrom: startDate,
                    DateTo: finishDate,
                    SiteName: siteName
                })
            });

            if (!res.ok) {
                throw new Error('Failed to fetch roster');
            }

            const response = await res.json();

            if (response.message != null) {
                console.log(response);
                setNoRosterDataMsg(response.message);
            } else {
                setRosterData(response);
                setFutureDateRestrict(response[0]?.futureDateRestrict || false);
                setNoRosterDataMsg(null);
            }
        } catch (ex) {
            console.error('Error fetching roster:', ex);
            showToast('Error', `Failed to fetch roster: ${ex.message}`);
        } finally {
            setLoading(false);
        }
    }, [deviceInfo, showToast]);

    const fetchRosterSummary = useCallback(async (startDate, finishDate, siteName) => {
        if (!deviceInfo) return;

        setLoading(true);
        try {
            const res = await authenticatedFetch('/ManagerReport/GetRosterSummary', {
                method: 'POST',
                body: JSON.stringify({
                    DateFrom: startDate,
                    DateTo: finishDate,
                    SiteName: siteName
                })
            });

            if (!res.ok) {
                throw new Error('Failed to fetch roster summary');
            }

            const response = await res.json();

            if (response.message != null) {
                console.log(response);
            } else {
                setRosterSummary(response);
            }
        } catch (ex) {
            console.error('Error fetching roster summary:', ex);
            showToast('Error', `Failed to fetch roster summary: ${ex.message}`);
        } finally {
            setLoading(false);
        }
    }, [deviceInfo, showToast]);

    const duplicateShifts = useCallback(async (duplicateDate, checkedRows, onSuccess) => {
        setLoading(true);
        const dateToDuplicateTo = new Date(duplicateDate);
        const targetWeekStart = getWeekStart(dateToDuplicateTo);

        const shiftsToDuplicate = Object.values(checkedRows).map(shift => ({
            shiftId: shift.shiftId,
            shiftStartDate: new Date(shift.shiftStart.split('T')[0]),
            codb: shift.codb
        }));

        try {
            const req = shiftsToDuplicate.map(({ shiftId, shiftStartDate, codb }) => {
                    const isoDow = shiftStartDate.getDay() === 0
                    ? 7
                    : shiftStartDate.getDay();

                const newShiftDate = new Date(targetWeekStart);
                newShiftDate.setDate(newShiftDate.getDate() + (isoDow - 1));

                const diffMs = newShiftDate.getTime() - shiftStartDate.getTime();
                const daysForward = Math.round(diffMs / (1000 * 60 * 60 * 24));

                return {
                    id: shiftId,
                    daysForward,
                    CODB: codb
                };
            });

            const res = await authenticatedFetch('/ManagerReport/stonegate__duplicateShifts', {
                method: 'POST',
                body: JSON.stringify(req)
            });

            if (!res.ok) {
                throw new Error('Network response was not ok');
            }

            showToast('Success', 'Shifts duplicated successfully!');

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error duplicating shifts:', err);
            showToast('Error', `Failed to duplicate shifts: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const duplicateWeek = useCallback(async (weekDate, destinationDate, onSuccess) => {
        setLoading(true);
        try {
            const weekStartDate = new Date(weekDate);
            const weekEndDate = new Date(weekStartDate);
            weekEndDate.setDate(weekEndDate.getDate() + 6);

            const shiftsInWeek = rosterData.filter(shift => {
                const shiftDate = new Date(shift.shiftStart);
                return shiftDate >= weekStartDate && shiftDate <= weekEndDate;
            });

            const shiftsToDuplicate = shiftsInWeek.map(shift => ({
                shiftId: shift.shiftId,
                shiftStartDate: new Date(shift.shiftStart),
                codb: shift.codb
            }));

            const destinationWeekStart = new Date(destinationDate);

            const req = shiftsToDuplicate.map(({ shiftId, shiftStartDate, codb }) => {
                const shiftDayOfWeek = shiftStartDate.getDay() === 0 ? 7 : shiftStartDate.getDay() + 1;

                const newShiftDate = new Date(destinationWeekStart);
                newShiftDate.setDate(newShiftDate.getDate() + (shiftDayOfWeek - 1));

                const diffMs = newShiftDate.getTime() - shiftStartDate.getTime();
                const daysForward = Math.round(diffMs / (1000 * 60 * 60 * 24));

                return {
                    id: shiftId,
                    daysForward,
                    CODB: codb
                };
            });

            const res = await authenticatedFetch('/ManagerReport/stonegate__duplicateShifts', {
                method: 'POST',
                body: JSON.stringify(req)
            });

            if (!res.ok) {
                throw new Error('Network response was not ok');
            }

            showToast('Success', 'Week duplicated successfully!');

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error duplicating week:', error);
            showToast('Error', `Failed to duplicate week: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [rosterData, showToast]);

    const removeShifts = useCallback(async (checkedRows, onSuccess) => {
        setLoading(true);
        const shiftsToRemove = Object.values(checkedRows).map(shift => ({
            shiftId: shift.shiftId,
            codb: shift.codb
        }));

        try {
            const req = shiftsToRemove.map(shift => ({
                RotshiftID: shift.shiftId,
                CODB: shift.codb
            }));

            const res = await authenticatedFetch('/ManagerReport/stonegate__deleteShift', {
                method: 'DELETE',
                body: JSON.stringify(req)
            });

            if (!res.ok) {
                throw new Error('Network response was not ok');
            }

            showToast('Success', 'Shifts removed successfully!');

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error removing shifts:', err);
            showToast('Error', `Failed to remove shifts: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    return {
        rosterData,
        setRosterData,
        rosterSummary,
        pendingShifts,
        setPendingShifts,
        completedPendingIds,
        markPendingAsCompleted,
        clearCompletedPendingShifts,
        loading,
        futureDateRestrict,
        noRosterDataMsg,
        toast,
        showToast,
        hideToast,
        fetchRoster,
        fetchRosterSummary,
        duplicateShifts,
        duplicateWeek,
        removeShifts
    };
};
